import { loadRuntimeConfig } from './config';
import './index.css';

/**
 * Application bootstrap.
 *
 * ── THE ORDERING CONTRACT ────────────────────────────────────────────────────────────────────────
 * `loadRuntimeConfig()` is awaited BEFORE `./App` is imported, and the import is dynamic precisely
 * so that it cannot be hoisted above the await. This is load-bearing, not tidiness:
 * `src/auth/msalConfig.ts` calls `new PublicClientApplication(msalConfig)` at MODULE scope, so it
 * reads `config.entraClientId` the moment it is first imported. A static `import { App }` at the
 * top of this file would be evaluated before the first line of code runs, and MSAL would be
 * constructed against an empty client id — producing an authentication failure at login rather
 * than an obvious error at boot.
 *
 * i18n is imported dynamically for the same reason: it is imported by the app tree, and keeping
 * every app-graph import behind the await means there is exactly one rule to remember instead of a
 * list of exceptions.
 */
async function bootstrap(): Promise<void> {
  await loadRuntimeConfig();

  const [{ default: React }, { default: ReactDOM }, { App }, i18nModule] = await Promise.all([
    import('react'),
    import('react-dom/client'),
    import('./App'),
    import('@/lib/i18n'),
  ]);

  const { default: i18n, applyDocumentLocale } = i18nModule;

  // Ensure <html lang>/<html dir> reflect the resolved locale before the React tree paints (the
  // inline script in index.html handles the dark class).
  applyDocumentLocale((i18n.resolvedLanguage ?? 'fr') as Parameters<typeof applyDocumentLocale>[0]);

  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

/**
 * If configuration cannot be resolved, say so ON THE PAGE.
 *
 * The alternative — letting the promise reject silently — leaves a blank white screen and an error
 * only in the console, which is indistinguishable from a broken deployment. A deployed build that
 * cannot find its `/config.json` is a deployment mistake with exactly one likely cause, so the page
 * names it. No configuration VALUES are printed: the message must be safe to show to any visitor.
 */
bootstrap().catch((err: unknown) => {
  const message = err instanceof Error ? err.message : String(err);
  // eslint-disable-next-line no-console -- last-resort diagnostic; the app never started.
  console.error('Sofexia HR failed to start:', err);

  const root = document.getElementById('root');
  if (!root) return;
  const box = document.createElement('div');
  box.setAttribute('role', 'alert');
  box.style.cssText =
    'max-width:40rem;margin:4rem auto;padding:1.5rem;font:14px/1.6 system-ui,sans-serif;' +
    'border:1px solid #d4322c;border-radius:8px;color:#7a1c18;background:#fdf3f2';
  const title = document.createElement('strong');
  title.textContent = 'Sofexia HR could not start';
  const detail = document.createElement('p');
  detail.textContent = message;
  const hint = document.createElement('p');
  hint.textContent =
    'This build resolves its settings at run time from /config.json. That file is deployed next to ' +
    'the application and is missing or unreadable. Re-run the deployment for this environment.';
  box.append(title, detail, hint);
  root.replaceChildren(box);
});
